import CrashReporter from "@/modules/app/CrashReporter";
import environment from "@/modules/environment";
import { addGuard } from "@/utils";

type AudioSource = string | Blob | ArrayBuffer | Uint8Array;

export default class AudioPlayer {
  private static ctx: AudioContext | null = null;
  private static sourceNode: AudioBufferSourceNode | null = null;
  private static gainNode: GainNode | null = null;
  private static status: "idle" | "playing" = "idle";
  private static stopTimerId: number | null = null;

  private static readonly MAX_LOOP_DURATION_MS = 30 * 60 * 1000; // 30 minutes

  static get isPlaying() {
    return this.status === "playing";
  }

  static async play(source: AudioSource, opts?: { loop?: boolean; volume?: number }) {
    try {
      if (this.isPlaying) {
        log.warn("AudioPlayer.play called while already playing; ignoring");
        return;
      }

      await this.ensureContext();
      const ctx = this.ctx;
      if (!ctx) throw Error("AudioPlayer.play: AudioContext not available");
      this.ensureGainNode();

      const audioBuffer = await this.loadAudioBuffer(ctx, environment.publicPath + source);
      if (!audioBuffer) throw Error("AudioPlayer.play: Failed to decode audio buffer");

      const isStoppedWhileLoading = this.ctx !== ctx;
      if (isStoppedWhileLoading) {
        log.media("AudioPlayer.play: stopped while loading, not starting playback");
        return;
      }

      const src = ctx.createBufferSource();
      src.buffer = audioBuffer;
      src.loop = !!opts?.loop;
      src.onended = () => this.cleanup();

      // volume control via gain node
      if (this.gainNode) src.connect(this.gainNode);
      else src.connect(ctx.destination);

      this.sourceNode = src;
      this.status = "playing";
      this.sourceNode.start(0);

      if (typeof opts?.volume === "number") this.setVolume(opts.volume);

      if (src.loop) this.startMaxLoopTimer();
    } catch (err) {
      log.err("'AudioPlayer.play' failed", err);
      CrashReporter.sendException("'AudioPlayer.play' failed", { key: "err", extra: err });
      this.stop();
      throw err;
    }
  }

  static stop() {
    try {
      if (this.stopTimerId !== null) {
        clearTimeout(this.stopTimerId);
        this.stopTimerId = null;
      }

      if (this.sourceNode) {
        this.sourceNode.onended = null as any;
        addGuard(() => this.sourceNode!.stop(0));
        addGuard(() => this.sourceNode!.disconnect());
      }
    } finally {
      this.cleanup();
    }
  }

  private static async ensureContext() {
    if (!this.ctx) this.ctx = new AudioContext();
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  private static ensureGainNode() {
    if (!this.ctx) return;
    if (!this.gainNode) {
      this.gainNode = this.ctx.createGain();
      this.gainNode.gain.value = 1;
      this.gainNode.connect(this.ctx.destination);
    }
  }

  private static async loadAudioBuffer(ctx: AudioContext, source: AudioSource): Promise<AudioBuffer> {
    let arrayBuffer: ArrayBuffer;
    if (typeof source === "string") {
      const response = await fetch(source);
      if (!response.ok) throw Error(`AudioPlayer: Failed to fetch source: ${response.status}`);
      const blob = await response.blob();
      arrayBuffer = await blob.arrayBuffer();
    } else if (source instanceof Blob) {
      arrayBuffer = await source.arrayBuffer();
    } else if (source instanceof Uint8Array) {
      arrayBuffer = source.slice().buffer;
    } else if (source instanceof ArrayBuffer) {
      arrayBuffer = source;
    } else {
      throw Error("AudioPlayer: Unsupported audio source type");
    }

    return await ctx.decodeAudioData(arrayBuffer.slice(0));
  }

  private static startMaxLoopTimer() {
    if (this.stopTimerId !== null) clearTimeout(this.stopTimerId);
    this.stopTimerId = window.setTimeout(() => {
      log.media("AudioPlayer: Max loop duration reached, stopping");
      this.stop();
    }, this.MAX_LOOP_DURATION_MS);
  }

  private static cleanup() {
    if (this.stopTimerId !== null) {
      clearTimeout(this.stopTimerId);
      this.stopTimerId = null;
    }

    if (this.sourceNode) {
      addGuard(() => this.sourceNode!.disconnect());
      this.sourceNode = null;
    }

    this.status = "idle";

    if (this.gainNode) {
      addGuard(() => this.gainNode!.disconnect());
      this.gainNode = null;
    }

    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      // Close asynchronously to avoid blocking callers
      ctx.close().catch((err) => {
        log.warn("AudioPlayer: Error closing AudioContext", err);
      });
    }
  }

  static setVolume(level: number) {
    const clamped = Math.max(0, Math.min(1, level));
    if (!this.ctx) return; // No context yet; volume applies after first play
    this.ensureGainNode();
    if (this.gainNode) this.gainNode.gain.value = clamped;
  }

  static getVolume(): number {
    return this.gainNode?.gain?.value ?? 1;
  }
}
